import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { StringFormatter } from '@repo/toolkit/string'
import { Option } from '@repo/toolkit/types'
import { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getTransfers } from '../../../../api/transfers/get-transfers'
import { getTripFolderMissionStaffs } from '../../../../api/trip-folder/get-trip-folder-mission-to-staffs'
import { TransferProperties } from '../../../../lib/types/models/transfer/transfer.properties'
import { TripBookingTypes } from '../../../../lib/types/models/trip-booking/trip-booking'
import { AssignTransferToTripFolderInput } from '../../../../lib/types/models/trip-folder/trip-folder-input'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import {
  createNumberArraySchema,
  createNumberSchema
} from '../../../../lib/utils/validations/int-array-validation'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../../elements/inputs/controlled-multi-select-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../../elements/validation-buttons'
import TripBookingBadge from './trip-booking-badge'

type TripFolderAssignTransferFormProps = BaseFormProps<AssignTransferToTripFolderInput, true> & {
  tripBooking: TripBookingTypes.Light
  tripFolderId: number
  preComponent?: React.ReactNode
}

type StaffOption = {
  firstName: string
  lastName: string
  missionLabel?: string
  staffType: 'mission-to-staff' | 'mission-to-external-staff'
  id: number
}

function mapStaffToOption(missionToStaff: StaffOption): Option<string> {
  return {
    label: `${missionToStaff.lastName.toUpperCase()} ${StringFormatter.capitalize(missionToStaff.firstName)} ${missionToStaff.missionLabel ? `- ${missionToStaff.missionLabel}` : ''}`,
    value: `mission-to-staff-id-${missionToStaff.id}`
  }
}

function mapExternalStaffToOption(missionToExternalStaff: StaffOption): Option<string> {
  return {
    label: `${StringFormatter.capitalize(missionToExternalStaff.firstName)} ${missionToExternalStaff.lastName.toUpperCase()} ${missionToExternalStaff.missionLabel ? `- ${missionToExternalStaff.missionLabel}` : ''}`,
    value: `mission-to-external-staff-id-${missionToExternalStaff.id}`
  }
}

const ASSING_TRANSFER_SCHEMA: ZodType<AssignTransferToTripFolderInput> = z
  .object({
    transferId: createNumberSchema('transfert'),
    numberOfPlacesDispatched: createNumberSchema('nomber des jeunes à placer', {
      minimumValue: 1
    }),
    missionToStaffIds: createNumberArraySchema('AAT / Externes', { nullish: true }),
    missionToExternalStaffIds: createNumberArraySchema('AAT / Externes', { nullish: true })
  })
  .superRefine((data, ctx) => {
    if (!data.missionToStaffIds?.length && !data.missionToExternalStaffIds?.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Veuillez sélectionner au moins un AAT ou un Externe',
        path: ['missionToStaffIds']
      })
    }
  })

const TripFolderAssignTransferForm = ({
  defaultValues,
  tripBooking,
  tripFolderId,
  preComponent,
  ...validationButtonProps
}: TripFolderAssignTransferFormProps) => {
  const form = useForm<AssignTransferToTripFolderInput>({
    resolver: zodResolver(ASSING_TRANSFER_SCHEMA),
    defaultValues,
    mode: FORM_MODE
  })

  const { missionToExternalStaffIds, missionToStaffIds } = form.watch()

  const { data: transfers } = useQuery({
    queryKey: ['transfers'],
    queryFn: getTransfers
  })

  const { data: staffs } = useQuery({
    queryKey: ['trip-folder-mission-staffs', tripFolderId],
    queryFn: () => getTripFolderMissionStaffs(tripFolderId)
  })

  // Memoize staff options for the select input
  const staffOptions = useMemo<Option<string>[]>(() => {
    if (!staffs) return []
    const missionToStaffs: StaffOption[] = staffs.missionToStaffs.map(
      ({ id, staff, mission }) => ({
        id,
        firstName: staff.firstName,
        lastName: staff.lastName,
        missionLabel: mission?.title,
        staffType: 'mission-to-staff'
      })
    )
    const missionToExternalStaffs: StaffOption[] = staffs.missionToExternalStaffs.map(
      ({ id, firstName, lastName, mission }) => ({
        id,
        firstName,
        lastName,
        missionLabel: mission?.title,
        staffType: 'mission-to-external-staff'
      })
    )
    return [
      ...missionToStaffs.map(mapStaffToOption),
      ...missionToExternalStaffs.map(mapExternalStaffToOption)
    ]
  }, [staffs])

  // Memoize selected staff objects for the selectedOptions prop
  const selectedStaffs = useMemo<StaffOption[]>(() => {
    if (!staffs) return []
    const missionToStaffs: StaffOption[] = staffs.missionToStaffs
      .filter((s) => missionToStaffIds?.includes(s.id))
      .map((s) => ({
        id: s.id,
        firstName: s.staff.firstName,
        lastName: s.staff.lastName,
        staffType: 'mission-to-staff'
      }))
    const missionToExternalStaffs: StaffOption[] = staffs.missionToExternalStaffs
      .filter((s) => missionToExternalStaffIds?.includes(s.id))
      .map((s) => ({
        id: s.id,
        firstName: s.firstName,
        lastName: s.lastName,
        staffType: 'mission-to-external-staff'
      }))
    return [...missionToStaffs, ...missionToExternalStaffs]
  }, [staffs, missionToStaffIds, missionToExternalStaffIds])

  // Memoize transfer selection function
  const getSelectedTransfer = useCallback(
    (id: number | undefined): Option<number> | undefined => {
      if (!id || !transfers?.data) return undefined
      const transfer = transfers.data.find((t) => t.id === id)

      if (transfer) return { value: id, label: TransferProperties.getLabel(transfer) }
      return undefined
    },
    [transfers]
  )

  // Memoize staff selection handler
  const handleStaffSelection = useCallback(
    (selectedIds: string[]): void => {
      const missionToStaffIds = selectedIds
        .filter((id) => id.startsWith('mission-to-staff-id-'))
        .map((id) => parseInt(id.replace('mission-to-staff-id-', ''), 10))
      const missionToExternalStaffIds = selectedIds
        .filter((id) => id.startsWith('mission-to-external-staff-id-'))
        .map((id) => parseInt(id.replace('mission-to-external-staff-id-', ''), 10))
      form.setValue('missionToStaffIds', missionToStaffIds)
      form.setValue('missionToExternalStaffIds', missionToExternalStaffIds)
    },
    [form]
  )

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col gap-3', MODAL_BODY_CLASSNAME)}>
          <TripBookingBadge
            lightTrip
            tripBooking={tripBooking}
            displayFormat={'compact'}
            infoType={'reserved'}
          />

          <FormField
            control={form.control}
            name={'transferId'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    dropDownPosition={'above'}
                    label={'Choisissez le transfert'}
                    options={transfers?.data.map((t) => ({
                      label: TransferProperties.getLabel(t),
                      value: t.id
                    }))}
                    selectedOption={getSelectedTransfer(field.value)}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'numberOfPlacesDispatched'}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput
                    {...field}
                    type={'number'}
                    label={'# Jeunes à placer'}
                    onChange={(val) => field.onChange(Number(val))}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="missionToStaffIds"
            render={({ field }) => (
              <FormItem className="flex-1 ">
                <FormControl>
                  <ControlledMultiSelectInput
                    {...field}
                    searchable
                    dropDownPosition="above"
                    variant="primary"
                    label={'AAT / Externes'}
                    options={staffOptions}
                    onChange={handleStaffSelection}
                    selectedOptions={[
                      ...selectedStaffs
                        .filter((s) => s.staffType === 'mission-to-staff')
                        .map(mapStaffToOption),
                      ...selectedStaffs
                        .filter((s) => s.staffType === 'mission-to-external-staff')
                        .map(mapExternalStaffToOption)
                    ]}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <ValidationButtons hasBorderTop preComponent={preComponent} {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default TripFolderAssignTransferForm
